/**
 * SessionContext — replaces the prototype's window.__CS__ state snapshot
 * and postMessage bridge (csNav, csSetEmail, csSetIdeas, csSaveScan,
 * csSaveLearn, csSetConf, csSetBooking, csCelebrate). Phase 1 holds local
 * state only; Phase 4 wires it to Firestore via the BFF.
 */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

/** A single idea in the multi-idea list. Mirrors the prototype's idea list. */
export interface IdeaSummary {
  id: string;
  name: string;
  updatedAt: number;
  scanPct: number;
}

/** Active booking summary shown on the home cover card. */
export interface BookingSummary {
  bookingId?: string;
  coachName: string;
  coachRole: string;
  slotLabel: string; // e.g. "Mon 04/08, 10:30 am"
  status: 'confirmed' | 'cancelled' | 'rescheduled';
}

/** One-time celebration flags. §12 conversion goal pages render once per user. */
export interface CelebrationFlags {
  scan?: boolean;
  learn?: boolean;
  booking?: boolean;
  /** §12 — first successful sign-in (renders /welcome once). */
  firstSignIn?: boolean;
}

export interface SessionState {
  /** Signed-in user's email (null when unauthenticated). */
  email: string | null;
  /** Multi-idea list. */
  ideas: IdeaSummary[];
  /** Active idea id (the one being edited). */
  activeIdeaId: string | null;
  /** Learning Hub overall progress, 0–100. */
  learningPct: number;
  /** Learning Hub modules completed during the current authenticated session. */
  learningCompletedModules: string[];
  /** Active booking, if any. */
  booking: BookingSummary | null;
  /** Confidentiality agreement in place. */
  confidentialityInPlace: boolean;
  /** One-time celebration flags. */
  celebrations: CelebrationFlags;
}

export interface SessionContextValue extends SessionState {
  setEmail: (email: string | null) => void;
  setIdeas: (ideas: IdeaSummary[]) => void;
  setActiveIdea: (ideaId: string | null) => void;
  setLearningPct: (pct: number) => void;
  setLearningCompletedModules: (moduleIds: string[]) => void;
  setBooking: (booking: BookingSummary | null) => void;
  setConfidentiality: (inPlace: boolean) => void;
  markCelebration: (key: keyof CelebrationFlags) => void;
  signOut: () => void;
}

const initialState: SessionState = {
  email: null,
  ideas: [],
  activeIdeaId: null,
  learningPct: 0,
  learningCompletedModules: [],
  booking: null,
  confidentialityInPlace: false,
  celebrations: {},
};

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>(initialState);

  const setEmail = useCallback((email: string | null) => {
    setState((s) => ({ ...s, email }));
  }, []);

  const setIdeas = useCallback((ideas: IdeaSummary[]) => {
    setState((s) => ({ ...s, ideas }));
  }, []);

  const setActiveIdea = useCallback((ideaId: string | null) => {
    setState((s) => ({ ...s, activeIdeaId: ideaId }));
  }, []);

  const setLearningPct = useCallback((pct: number) => {
    setState((s) => ({ ...s, learningPct: Math.max(0, Math.min(100, pct)) }));
  }, []);

  const setLearningCompletedModules = useCallback((moduleIds: string[]) => {
    setState((s) => ({ ...s, learningCompletedModules: [...new Set(moduleIds)] }));
  }, []);

  const setBooking = useCallback((booking: BookingSummary | null) => {
    setState((s) => ({ ...s, booking }));
  }, []);

  const setConfidentiality = useCallback((inPlace: boolean) => {
    setState((s) => ({ ...s, confidentialityInPlace: inPlace }));
  }, []);

  const markCelebration = useCallback((key: keyof CelebrationFlags) => {
    setState((s) => ({ ...s, celebrations: { ...s.celebrations, [key]: true } }));
  }, []);

  const signOut = useCallback(() => {
    setState(initialState);
  }, []);

  const value: SessionContextValue = {
    ...state,
    setEmail,
    setIdeas,
    setActiveIdea,
    setLearningPct,
    setLearningCompletedModules,
    setBooking,
    setConfidentiality,
    markCelebration,
    signOut,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within a SessionProvider');
  return ctx;
}